import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  LogOut,
  Settings,
  SlidersHorizontal,
  Building2,
  Phone,
  Users,
  BarChart3,
  Calendar,
  MessageSquare,
  LayoutDashboard,
  FileText,
  CreditCard,
  Search,
  HelpCircle,
  Info,
  FlaskConical,
} from 'lucide-react';

import { Logo } from '../ui/Logo';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import SearchBar from '../ui/SearchBar';
import { useUserStore } from '../../stores/userStore';
import { useAnalytics } from '../../hooks/useAnalytics';

const SCROLL_THRESHOLD = 80;

/** Matches QuickStartGuide / HowItWorksSection icon treatment */
const landingNavIconShell =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600';

export default function Header() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { user, clearUser } = useUserStore();
  const { quickTrack, trackAuth, trackNavigation, trackSearch } = useAnalytics();

  const isHome = location.pathname === '/';
  const isOverlay = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    handleScroll(); // set initial state
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path ? 'text-primary-600' : 'text-neutral-700';
  };

  const navLinkStyles = isOverlay
    ? 'text-white hover:text-primary-200 font-medium'
    : 'nav-link';
  const navLinkActive = (path: string) =>
    isOverlay ? (location.pathname === path ? 'text-primary-200' : '') : isActive(path);

  // Helper function to get user type
  const getUserType = () => {
    return user?.userType || 'tenant';
  };

  // Helper function to get role-specific navigation items
  const getRoleNavigationItems = () => {
    const userType = getUserType();
    
    switch (userType) {
      case 'tenant':
        return [
          { to: '/dashboard/tenant', label: 'Dashboard', icon: LayoutDashboard },
          { to: '/dashboard/tenant/applications', label: 'My Applications', icon: FileText },
          { to: '/dashboard/tenant/amenities', label: 'Amenities', icon: Building2 },
          { to: '/dashboard/tenant/messages', label: 'Messages', icon: MessageSquare },
          { to: '/dashboard/tenant/payments', label: 'Payments', icon: CreditCard },
        ];
      case 'landlord':
        return [
          { to: '/dashboard/landlord', label: 'Dashboard', icon: LayoutDashboard },
          { to: '/dashboard/landlord/properties', label: 'My Properties', icon: Building2 },
          { to: '/dashboard/landlord/applications', label: 'Applications', icon: FileText },
          { to: '/dashboard/landlord/tenants', label: 'Tenants', icon: Users },
          { to: '/dashboard/landlord/maintenance', label: 'Maintenance', icon: Settings },
          { to: '/dashboard/landlord/messages', label: 'Messages', icon: MessageSquare },
          { to: '/dashboard/landlord/reports', label: 'Reports', icon: BarChart3 },
        ];
      case 'propertyManager':
      case 'agent':
        return [
          { to: '/dashboard/agent', label: 'Dashboard', icon: LayoutDashboard },
          { to: '/dashboard/agent/clients', label: 'Clients', icon: Users },
          { to: '/dashboard/agent/commission', label: 'Commission', icon: CreditCard },
          { to: '/dashboard/agent/calendar', label: 'Calendar', icon: Calendar },
          { to: '/dashboard/agent/leads', label: 'Leads', icon: FileText },
        ];
      default:
        return [
          { to: '/dashboard/tenant', label: 'Dashboard', icon: LayoutDashboard },
        ];
    }
  };

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    quickTrack.home();
    setIsMobileMenuOpen((open) => !open);
  }, [quickTrack]);

  const handleLogout = async () => {
    try {
      quickTrack.signOut();
      trackAuth('logout');
      const { supabase } = await import('../../lib/supabase');
      await supabase.auth.signOut();
    } catch {
      // ignore sign out errors for now
    } finally {
      clearUser();
      navigate('/');
      closeMobileMenu();
    }
  };

  // Close overlays when route changes
  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileMenuOpen]);

  // Enhanced search submission with analytics
  const handleSearchSubmit = () => {
    quickTrack.searchSubmit(searchQuery);
    trackSearch(searchQuery);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  // Enhanced mobile search submission with analytics
  const handleMobileSearchSubmit = () => {
    quickTrack.searchSubmit(searchQuery);
    trackSearch(searchQuery);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    closeMobileMenu();
  };

  // Enhanced navigation link tracking
  const handleNavClick = (destination: string) => {
    trackNavigation(destination, location.pathname);
    quickTrack.home(); // Track general navigation
  };

  // Handle window resize — close drawer when crossing into large-screen layout (Tailwind lg)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const roleNavigationItems = getRoleNavigationItems();

  return (
    <header
      className={`relative sticky top-0 z-50 overflow-visible ${
        isOverlay
          ? 'border-b border-white/15 bg-neutral-950/90 shadow-lg shadow-black/20'
          : 'bg-white shadow-sm border-b border-neutral-100'
      } ${isOverlay ? 'header-overlay' : ''}`}
    >
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex h-[4.5rem] min-h-[4.5rem] items-center justify-between gap-2 sm:h-20 sm:min-h-20 lg:h-24 lg:min-h-24 lg:gap-3 xl:h-[120px] xl:min-h-[120px] xl:gap-4">
          {/* Logo with analytics */}
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center justify-center"
            onClick={() => handleNavClick('/')}
          >
            <Logo
              className={`h-11 w-auto max-w-[160px] sm:h-12 sm:max-w-[180px] lg:h-[4.25rem] lg:max-w-[220px] xl:h-24 xl:max-w-[min(100%,280px)] ${isOverlay ? 'drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)] brightness-110' : ''}`}
            />
          </Link>

          {/* Desktop Navigation: from lg up; compact lg–xl for laptops without crowding */}
          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-2 text-xs font-medium lg:flex xl:gap-4 xl:text-sm 2xl:gap-5">
            <Link
              to="/properties"
              className={`inline-flex min-w-0 max-w-[9.5rem] shrink items-center gap-1 xl:max-w-none xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/properties')}`}
              onClick={() => handleNavClick('/properties')}
              title="Browse homes"
            >
              <Search className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="truncate">Browse homes</span>
            </Link>
            <Link
              to="/register-enhanced?role=landlord"
              className={`inline-flex min-w-0 max-w-[8rem] shrink items-center gap-1 xl:max-w-none xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/register-enhanced')}`}
              onClick={() => handleNavClick('/register-enhanced')}
              title="For landlords"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="truncate">For landlords</span>
            </Link>
            <Link
              to="/agents"
              className={`inline-flex min-w-0 max-w-[7rem] shrink items-center gap-1 xl:max-w-none xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/agents')}`}
              onClick={() => handleNavClick('/agents')}
              title="Find Agents"
            >
              <Users className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="truncate">Find Agents</span>
            </Link>
            <Link
              to="/about"
              className={`inline-flex min-w-0 max-w-[6rem] shrink items-center gap-1 xl:max-w-[10rem] 2xl:max-w-none xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/about')}`}
              onClick={() => handleNavClick('/about')}
              title={t('footer.about')}
            >
              <Info className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="truncate">{t('footer.about')}</span>
            </Link>
            <Link
              to="/help"
              className={`inline-flex items-center gap-1 xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/help')}`}
              onClick={() => handleNavClick('/help')}
              title={t('footer.help')}
              aria-label={t('footer.help')}
            >
              <HelpCircle className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="hidden xl:inline">{t('footer.help')}</span>
            </Link>
            <Link
              to="/contact"
              className={`inline-flex items-center gap-1 xl:gap-1.5 ${navLinkStyles} ${navLinkActive('/contact')}`}
              onClick={() => handleNavClick('/contact')}
              title={t('footer.contact')}
              aria-label={t('footer.contact')}
            >
              <Phone className="h-3.5 w-3.5 shrink-0 opacity-90 xl:h-4 xl:w-4" aria-hidden />
              <span className="hidden xl:inline">{t('footer.contact')}</span>
            </Link>
            {!isHome && (
              <div className="relative min-w-0 shrink lg:max-w-[10.5rem] xl:max-w-[14rem] 2xl:w-64 2xl:max-w-none">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  className="w-full"
                  onSubmit={handleSearchSubmit}
                />
              </div>
            )}
            <div
              className={
                isOverlay
                  ? '[&_button]:text-white [&_button]:hover:bg-white/10 [&_button]:hover:text-primary-200 [&_span]:text-white'
                  : ''
              }
            >
              <LanguageSwitcher variant="toolbar" />
            </div>
            {process.env.NODE_ENV === 'development' && (
              <Link
                to="/test-auth"
                aria-label="Test Auth"
                className={`inline-flex items-center gap-1 text-xs xl:gap-1.5 xl:text-sm ${isOverlay ? 'text-white hover:text-primary-200' : 'nav-link hover:text-primary-600'}`}
                onClick={() => handleNavClick('/test-auth')}
              >
                <FlaskConical className="h-3.5 w-3.5 shrink-0 opacity-90 text-current xl:h-4 xl:w-4" aria-hidden />
                <span className="hidden 2xl:inline">Test Auth</span>
              </Link>
            )}
          </nav>

          {/* User Actions - Desktop (same lg breakpoint as primary nav) */}
          <div className="hidden shrink-0 items-center justify-center gap-2 lg:flex xl:gap-4">
            {user ? (
              <div className="relative user-menu">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex min-w-0 max-w-[11rem] items-center gap-1.5 text-xs font-medium xl:max-w-none xl:gap-2 xl:text-sm ${
                    isOverlay ? 'text-white hover:text-primary-200' : 'text-neutral-700 hover:text-primary-600'
                  }`}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                  aria-controls="user-menu-dropdown"
                  aria-label={`Account menu for ${user.fullName}`}
                >
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isOverlay ? 'bg-white/25 ring-1 ring-white/30' : 'bg-primary-100'}`}>
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt=""
                        width={32}
                        height={32}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <User className={`h-5 w-5 ${isOverlay ? 'text-white' : 'text-primary-600'}`} />
                    )}
                  </div>
                  <span className="truncate">
                    Welcome, {user.fullName.split(' ')[0]}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <div
                    id="user-menu-dropdown"
                    className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-neutral-100 py-1 z-50"
                  >
                    <div className="px-4 py-2 text-xs uppercase text-primary-600 font-semibold tracking-wide">
                      {getUserType() === 'tenant' ? 'Tenant' : 
                       getUserType() === 'landlord' ? 'Landlord' : 
                       getUserType() === 'propertyManager' ? 'Property Manager' : 'User'} Dashboard
                    </div>
                    {roleNavigationItems.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <span
                            className="mr-2.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600"
                            aria-hidden
                          >
                            <IconComponent className="h-4 w-4" />
                          </span>
                          {item.label}
                        </Link>
                      );
                    })}
                    <div className="my-1 border-t border-neutral-200" />
                    <Link
                      to="/dashboard/settings"
                      className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleNavClick('/dashboard/settings');
                      }}
                    >
                      <span
                        className="mr-2.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600"
                        aria-hidden
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                      </span>
                      Settings
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-red-50 hover:text-red-600"
                    >
                      <span
                        className="mr-2.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600"
                        aria-hidden
                      >
                        <LogOut className="h-4 w-4" />
                      </span>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/signin"
                  className={`rounded-lg px-3 py-2 text-xs font-medium xl:px-4 xl:text-sm ${
                    isOverlay
                      ? 'text-white hover:text-primary-200'
                      : 'text-neutral-700 hover:text-primary-600'
                  }`}
                  onClick={() => {
                    quickTrack.signIn();
                    handleNavClick('/signin');
                  }}
                >
                  Sign in
                </Link>
                <Link
                  to="/register-enhanced"
                  title="List a property"
                  aria-label="List a property"
                  className={`rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 xl:px-4 xl:text-sm ${
                    isOverlay
                      ? 'border border-primary-500 bg-primary-600 text-white hover:bg-primary-500 focus:ring-primary-400'
                      : 'btn-primary'
                  }`}
                  onClick={() => {
                    quickTrack.signUp();
                    handleNavClick('/register-enhanced');
                  }}
                >
                  <span className="xl:hidden">List</span>
                  <span className="hidden xl:inline">List a property</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            id="mobileMenuButton"
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 lg:hidden ${
              isOverlay
                ? 'text-white hover:bg-white/15 focus:ring-white/50'
                : 'text-neutral-700 hover:bg-neutral-100 hover:text-primary-600'
            }`}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            aria-expanded={isMobileMenuOpen}
            type="button"
          >
            <div
              id="hamburgerIcon"
              className={`flex flex-col items-center justify-center w-6 h-6 ${isMobileMenuOpen ? 'hamburger-icon-open' : ''}`}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </div>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            id="mobileMenuOverlay"
            className="fixed inset-0 z-40 bg-black/65 lg:hidden"
            onClick={closeMobileMenu}
            aria-hidden
          />

          <div
            id="mobileMenu"
            className="absolute left-0 right-0 top-full z-50 max-h-[min(70vh,calc(100dvh-4.5rem))] overflow-y-auto border-t border-neutral-200 bg-white shadow-xl sm:max-h-[min(70vh,calc(100dvh-5rem))] lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
        <div className="py-4">
          {/* Search Bar - Prominent in Mobile */}
          <div className="px-4 mb-6">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              className="w-full"
              onSubmit={handleMobileSearchSubmit}
            />
          </div>

          {/* Navigation Links */}
          <nav className="space-y-1 px-4 mb-6" role="navigation" aria-label="Main menu">
            <Link
              to="/properties"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/properties');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <Search className="h-5 w-5" />
              </span>
              <span>{t('nav.rent')}</span>
            </Link>
            <Link
              to="/register-enhanced?role=landlord"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/register-enhanced');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <Building2 className="h-5 w-5" />
              </span>
              <span>{t('help.forLandlords')}</span>
            </Link>
            <Link
              to="/agents"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/agents');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <Users className="h-5 w-5" />
              </span>
              <span>Find Agents</span>
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/about');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <Info className="h-5 w-5" />
              </span>
              <span>{t('footer.about')}</span>
            </Link>
            <Link
              to="/help"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/help');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <HelpCircle className="h-5 w-5" />
              </span>
              <span>{t('footer.help')}</span>
            </Link>
            <Link
              to="/contact"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
              onClick={() => {
                closeMobileMenu();
                handleNavClick('/contact');
              }}
            >
              <span className={landingNavIconShell} aria-hidden>
                <Phone className="h-5 w-5" />
              </span>
              <span>{t('footer.contact')}</span>
            </Link>
            {process.env.NODE_ENV === 'development' && (
              <Link
                to="/test-auth"
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                onClick={() => {
                  closeMobileMenu();
                  handleNavClick('/test-auth');
                }}
              >
                <span className={landingNavIconShell} aria-hidden>
                  <FlaskConical className="h-5 w-5" />
                </span>
                <span>Test Auth</span>
              </Link>
            )}
          </nav>

          {/* Language Switcher */}
          <div className="px-4 mb-6">
            <LanguageSwitcher />
          </div>

          {/* User Section */}
          <div className="border-t border-neutral-200 pt-4">
            {user ? (
              <div className="px-4">
                {/* User Info */}
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.fullName}
                        width={48}
                        height={48}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary-600" />
                      </div>
                    )}
                  </div>
                    <div className="ml-3">
                    <div className="text-base font-medium text-neutral-900">{user.fullName}</div>
                    <div className="text-sm text-neutral-500 capitalize">
                      {getUserType() === 'tenant' ? 'Tenant' : 
                       getUserType() === 'landlord' ? 'Landlord' : 
                       getUserType() === 'propertyManager' ? 'Property Manager' : 'User'}
                    </div>
                  </div>
                </div>

                {/* Role-Specific Navigation */}
                <div className="space-y-1 mb-4">
                  <div className="px-3 py-2 text-xs uppercase text-primary-600 font-semibold tracking-wide">
                    {getUserType() === 'tenant' ? 'Tenant' : 
                     getUserType() === 'landlord' ? 'Landlord' : 
                     getUserType() === 'propertyManager' ? 'Property Manager' : 'User'} Menu
                  </div>
                  {roleNavigationItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 rounded-xl"
                        onClick={() => {
                          closeMobileMenu();
                          handleNavClick(item.to);
                        }}
                      >
                        <span className={landingNavIconShell} aria-hidden>
                          <IconComponent className="h-5 w-5" />
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>

                {/* Settings & Logout */}
                <div className="space-y-1">
                  <Link
                    to="/dashboard/settings"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 rounded-xl"
                    onClick={closeMobileMenu}
                  >
                    <span className={landingNavIconShell} aria-hidden>
                      <SlidersHorizontal className="h-5 w-5" />
                    </span>
                    <span>Settings</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl w-full text-left"
                  >
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600"
                      aria-hidden
                    >
                      <LogOut className="h-5 w-5" />
                    </span>
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            ) : (
                <div className="px-4 space-y-3">
                <Link
                  to="/signin"
                  className="block w-full text-center px-4 py-3.5 text-base font-medium text-neutral-700 border border-neutral-300 rounded-xl hover:bg-neutral-50"
                  onClick={() => {
                    closeMobileMenu();
                    quickTrack.signIn();
                    handleNavClick('/signin');
                  }}
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register-enhanced"
                  className="block w-full text-center px-4 py-3.5 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  onClick={() => {
                    closeMobileMenu();
                    quickTrack.signUp();
                    handleNavClick('/register-enhanced');
                  }}
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>
        </div>
          </div>
        </>
      )}
    </header>
  );
}